import React, { useEffect, useState } from "react";
import { promoterApi } from "../../api/client";
import { DocumentViewer } from "../../Components/DocumentViewer";
import { useAuth } from "../../context/AuthContext";

export default function PromoterProfile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    fatherName: "",
    bloodGroup: "",
    qualification: "",
    panNumber: "",
    address: {
      street: "",
      city: "",
      state: "",
      pincode: "",
      country: "India",
    },
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await promoterApi.getProfile();

      // Extract profile data - backend returns it as 'profile' key
      const profileData =
        response?.profile || response?.promoterProfile || response;
      setProfile(profileData);

      // Populate form data with all available fields
      setFormData({
        name: profileData?.name || "",
        fatherName: profileData?.fatherName || "",
        bloodGroup: profileData?.bloodGroup || "",
        qualification: profileData?.qualification || "",
        panNumber: profileData?.panNumber || "",
        address: {
          street: profileData?.address?.street || "",
          city: profileData?.address?.city || "",
          state: profileData?.address?.state || "",
          pincode: profileData?.address?.pincode || "",
          country: profileData?.address?.country || "India",
        },
        commissionRate: profileData?.commissionRate || 0,
        totalProvidersCreated: profileData?.totalProvidersCreated || 0,
        totalListingsUnderPromoter:
          profileData?.totalListingsUnderPromoter || 0,
        paymentDetails: profileData?.paymentDetails || {
          amount: 5000,
          utrId: "",
          paymentStatus: "pending",
        },
      });
    } catch (err) {
      setError("Failed to fetch profile");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    if (field.includes(".")) {
      const [parent, child] = field.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      setError("");

      const response = await promoterApi.updateProfile(formData);

      // Refresh profile data
      await fetchProfile();
      setEditing(false);
      alert("Profile updated successfully!");
    } catch (err) {
      console.error("Update error:", err);
      setError(err?.data?.message || "Failed to update profile");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    // Reset form data to original profile data
    setFormData({
      name: profile?.name || "",
      fatherName: profile?.fatherName || "",
      bloodGroup: profile?.bloodGroup || "",
      qualification: profile?.qualification || "",
      panNumber: profile?.panNumber || "",
      address: {
        street: profile?.address?.street || "",
        city: profile?.address?.city || "",
        state: profile?.address?.state || "",
        pincode: profile?.address?.pincode || "",
        country: profile?.address?.country || "India",
      },
    });
    setEditing(false);
    setError("");
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-300 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-300 rounded"></div>
            <div className="h-4 bg-gray-300 rounded w-5/6"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="p-6">
        <div className="text-gray-500">No profile found</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">My Profile</h1>
        {!editing && (
          <button
            onClick={() => setEditing(true)}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Edit Profile
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      {/* User Account Information */}
      {user ? (
        <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-200">
          <h2 className="text-xl font-semibold mb-4 text-blue-800 flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-sm">
                {user?.email?.charAt(0).toUpperCase() || "U"}
              </span>
            </div>
            Account Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white p-4 rounded-lg border shadow-sm">
              <h3 className="font-semibold text-gray-800 mb-3 text-sm uppercase tracking-wide">
                Basic Details
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">User ID:</span>
                  <span className="font-medium text-gray-900">
                    {user.id || user._id || "N/A"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Email:</span>
                  <span className="font-medium text-gray-900">
                    {user.email || "N/A"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Phone:</span>
                  <span className="font-medium text-gray-900">
                    {user.phoneNumber || user.phone || "N/A"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Role:</span>
                  <span className="font-medium text-blue-600 capitalize">
                    {user.role || "N/A"}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg border shadow-sm">
              <h3 className="font-semibold text-gray-800 mb-3 text-sm uppercase tracking-wide">
                Account Status
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Status:</span>
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      user.status === "active"
                        ? "bg-green-100 text-green-800"
                        : user.status === "pending"
                        ? "bg-yellow-100 text-yellow-800"
                        : user.status === "suspended"
                        ? "bg-red-100 text-red-800"
                        : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {user.status || "Unknown"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Payment Status:</span>
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      user.paymentStatus === "verified"
                        ? "bg-green-100 text-green-800"
                        : user.paymentStatus === "pending"
                        ? "bg-yellow-100 text-yellow-800"
                        : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {user.paymentStatus || "Not Set"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Created:</span>
                  <span className="font-medium text-gray-900">
                    {user.createdAt
                      ? new Date(user.createdAt).toLocaleDateString()
                      : "N/A"}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg border shadow-sm">
              <h3 className="font-semibold text-gray-800 mb-3 text-sm uppercase tracking-wide">
                Additional Info
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Channel Partner:</span>
                  <span className="font-medium text-gray-900">
                    {user.channelPartnerId ? "Linked" : "Not Linked"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Last Login:</span>
                  <span className="font-medium text-gray-900">
                    {user.lastLoginAt
                      ? new Date(user.lastLoginAt).toLocaleDateString()
                      : "Never"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Profile Complete:</span>
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      profile?.status === "approved"
                        ? "bg-green-100 text-green-800"
                        : profile?.status === "pending_approval"
                        ? "bg-yellow-100 text-yellow-800"
                        : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {profile?.status === "approved"
                      ? "Complete"
                      : profile?.status === "pending_approval"
                      ? "Pending"
                      : "Incomplete"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="mb-6 bg-red-50 p-4 rounded-lg border border-red-200">
          <h2 className="text-lg font-semibold mb-2 text-red-800">
            No User Data Available
          </h2>
          <p className="text-red-600">
            Unable to load user information. Please check your authentication
            status.
          </p>
        </div>
      )}

      {/* Complete Profile Information Display */}
      {profile && (
        <div className="mb-8 bg-gradient-to-r from-green-50 to-blue-50 p-6 rounded-lg border border-green-200">
          <h2 className="text-xl font-semibold mb-4 text-green-800 flex items-center gap-2">
            <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-sm">
                {profile?.name?.charAt(0).toUpperCase() || "P"}
              </span>
            </div>
            Complete Profile Information
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Personal Information */}
            <div className="bg-white p-4 rounded-lg border shadow-sm">
              <h3 className="font-semibold text-gray-800 mb-3 text-sm uppercase tracking-wide">
                Personal Information
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Name:</span>
                  <span className="font-medium text-gray-900">
                    {profile.name || "-"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Father's Name:</span>
                  <span className="font-medium text-gray-900">
                    {profile.fatherName || "-"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Email:</span>
                  <span className="font-medium text-gray-900">
                    {profile.email || "-"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Phone:</span>
                  <span className="font-medium text-gray-900">
                    {user.phoneNumber || "-"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Blood Group:</span>
                  <span className="font-medium text-gray-900">
                    {profile.bloodGroup || "-"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Qualification:</span>
                  <span className="font-medium text-gray-900">
                    {profile.qualification || "-"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">PAN Number:</span>
                  <span className="font-medium text-gray-900">
                    {profile.panNumber || "-"}
                  </span>
                </div>
              </div>
            </div>

            {/* Address Information */}
            <div className="bg-white p-4 rounded-lg border shadow-sm">
              <h3 className="font-semibold text-gray-800 mb-3 text-sm uppercase tracking-wide">
                Address
              </h3>
              {profile.address ? (
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Street:</span>
                    <span className="font-medium text-gray-900">
                      {profile.address.street || "-"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">City:</span>
                    <span className="font-medium text-gray-900">
                      {profile.address.city || "-"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">State:</span>
                    <span className="font-medium text-gray-900">
                      {profile.address.state || "-"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Pincode:</span>
                    <span className="font-medium text-gray-900">
                      {profile.address.pincode || "-"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Country:</span>
                    <span className="font-medium text-gray-900">
                      {profile.address.country || "-"}
                    </span>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500">No address provided</p>
              )}
            </div>

            {/* Status & Payment Information */}
            <div className="bg-white p-4 rounded-lg border shadow-sm">
              <h3 className="font-semibold text-gray-800 mb-3 text-sm uppercase tracking-wide">
                Status & Payment
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Profile Status:</span>
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      profile.status === "approved"
                        ? "bg-green-100 text-green-800"
                        : profile.status === "pending_approval"
                        ? "bg-yellow-100 text-yellow-800"
                        : profile.status === "pending_verification"
                        ? "bg-blue-100 text-blue-800"
                        : profile.status === "rejected"
                        ? "bg-red-100 text-red-800"
                        : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {profile.status?.replace(/_/g, " ") || "-"}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Payment Status:</span>
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      profile.paymentDetails?.paymentStatus === "verified"
                        ? "bg-green-100 text-green-800"
                        : profile.paymentDetails?.paymentStatus === "pending"
                        ? "bg-yellow-100 text-yellow-800"
                        : profile.paymentDetails?.paymentStatus === "rejected"
                        ? "bg-red-100 text-red-800"
                        : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {profile.paymentDetails?.paymentStatus || "-"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Payment Amount:</span>
                  <span className="font-medium text-gray-900">
                    {profile.paymentDetails?.amount
                      ? `₹${profile.paymentDetails.amount}`
                      : "-"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">UTR ID:</span>
                  <span className="font-medium text-gray-900">
                    {profile.paymentDetails?.utrId || "-"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Commission Rate:</span>
                  <span className="font-medium text-gray-900">
                    {profile.commissionRate
                      ? `${profile.commissionRate}%`
                      : "-"}
                  </span>
                </div>
              </div>
            </div>

            {/* Statistics */}
            <div className="bg-white p-4 rounded-lg border shadow-sm">
              <h3 className="font-semibold text-gray-800 mb-3 text-sm uppercase tracking-wide">
                Statistics
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Providers Created:</span>
                  <span className="font-medium text-gray-900">
                    {profile.totalProvidersCreated || 0}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">
                    Listings Under Promoter:
                  </span>
                  <span className="font-medium text-gray-900">
                    {profile.totalListingsUnderPromoter || 0}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Created At:</span>
                  <span className="font-medium text-gray-900">
                    {profile.createdAt
                      ? new Date(profile.createdAt).toLocaleDateString()
                      : "-"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Verified At:</span>
                  <span className="font-medium text-gray-900">
                    {profile.verifiedAt
                      ? new Date(profile.verifiedAt).toLocaleDateString()
                      : "-"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Approved At:</span>
                  <span className="font-medium text-gray-900">
                    {profile.approvedAt
                      ? new Date(profile.approvedAt).toLocaleDateString()
                      : "-"}
                  </span>
                </div>
              </div>
            </div>

            {/* Documents Information */}
            <div className="bg-white p-4 rounded-lg border shadow-sm">
              <h3 className="font-semibold text-gray-800 mb-3 text-sm uppercase tracking-wide">
                Documents
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Experience Documents:</span>
                  <span className="font-medium text-gray-900">
                    {profile.documents?.experienceDocuments ? "✓" : "✗"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">PAN Card Image:</span>
                  <span className="font-medium text-gray-900">
                    {profile.documents?.panCardImage ? "✓" : "✗"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">KYC Document:</span>
                  <span className="font-medium text-gray-900">
                    {profile.documents?.kycDocument ? "✓" : "✗"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">KYC Image:</span>
                  <span className="font-medium text-gray-900">
                    {profile.documents?.kycImage ? "✓" : "✗"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">CV Upload:</span>
                  <span className="font-medium text-gray-900">
                    {profile.documents?.cvUpload ? "✓" : "✗"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Photo Upload:</span>
                  <span className="font-medium text-gray-900">
                    {profile.documents?.photoUpload ? "✓" : "✗"}
                  </span>
                </div>
              </div>
            </div>

            {/* Additional Information */}
            <div className="bg-white p-4 rounded-lg border shadow-sm">
              <h3 className="font-semibold text-gray-800 mb-3 text-sm uppercase tracking-wide">
                Additional Info
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Profile ID:</span>
                  <span className="font-medium text-gray-900 text-xs">
                    {profile.id || profile._id || "-"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Channel Partner ID:</span>
                  <span className="font-medium text-gray-900 text-xs">
                    {profile.channelPartnerId || "-"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Is Active:</span>
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      profile.isActive
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {profile.isActive ? "Yes" : "No"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Last Activity:</span>
                  <span className="font-medium text-gray-900">
                    {profile.lastActivityAt
                      ? new Date(profile.lastActivityAt).toLocaleDateString()
                      : "-"}
                  </span>
                </div>
                {profile.rejectionReason && (
                  <div className="mt-2 p-2 bg-red-50 rounded text-xs">
                    <span className="text-red-600 font-medium">
                      Rejection Reason:
                    </span>
                    <p className="text-red-700 mt-1">
                      {profile.rejectionReason}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Documents Section */}
      <div className="mt-6">
        <DocumentViewer documents={profile.documents} title="My Documents" />
      </div>
    </div>
  );
}
